import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    GitBranch,
    Leaf,
    DollarSign,
    TrendingDown,
    Play,
    RotateCcw,
    Zap,
    Clock,
    Map as MapIcon,
    CheckCircle2,
    Cpu
} from 'lucide-react';
import { toast } from 'sonner';

interface Point {
    x: number;
    y: number;
    id: number;
}

interface SmartRouteOptimizerProps {
    autoStart?: boolean;
    onComplete?: () => void;
    className?: string;
}

export default function SmartRouteOptimizer({
    autoStart = false,
    onComplete,
    className = ""
}: SmartRouteOptimizerProps) {
    const [points, setPoints] = useState<Point[]>([]);
    const [route, setRoute] = useState<number[]>([]);
    const [isSolving, setIsSolving] = useState(false);
    const [generation, setGeneration] = useState(0);
    const [bestDistance, setBestDistance] = useState(0);
    const [initialDistance, setInitialDistance] = useState(0);
    const [solved, setSolved] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>();

    // Initialize random wells
    useEffect(() => {
        resetSimulation();
    }, []);

    // Auto-start logic
    useEffect(() => {
        if (autoStart && points.length > 0 && !isSolving && !solved) {
            const timer = setTimeout(() => {
                solve();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [autoStart, points]);

    const resetSimulation = () => {
        const parent = canvasRef.current?.parentElement;
        const width = parent?.clientWidth || 600;
        const height = parent?.clientHeight || 400;

        // Create random cluster of wells
        const newPoints = Array.from({ length: 15 }).map((_, i) => ({
            x: Math.random() * (width - 100) + 50,
            y: Math.random() * (height - 100) + 50,
            id: i
        }));

        setPoints(newPoints);

        // Initial Route (0, 1, 2, ... n) - CHAOS
        const initial = newPoints.map((_, i) => i);
        setRoute(initial);

        const dist = calculateDistance(newPoints, initial);
        setInitialDistance(dist);
        setBestDistance(dist);
        setGeneration(0);
        setSolved(false);
        setIsSolving(false);

        if (animationRef.current) cancelAnimationFrame(animationRef.current);

        // Draw initial state
        setTimeout(() => draw(newPoints, initial), 10);
    };

    const calculateDistance = (pts: Point[], path: number[]) => {
        let dist = 0;
        for (let i = 0; i < path.length - 1; i++) {
            const p1 = pts[path[i]];
            const p2 = pts[path[i + 1]];
            dist += Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        }
        // Return to start
        const pLast = pts[path[path.length - 1]];
        const pFirst = pts[path[0]];
        dist += Math.sqrt(Math.pow(pFirst.x - pLast.x, 2) + Math.pow(pFirst.y - pLast.y, 2));

        return dist;
    };

    const draw = (pts: Point[], path: number[], color?: string) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Check for dark mode
        const isDark = document.documentElement.classList.contains('dark');
        
        // Colors
        const bgColor = isDark ? '#0f172a' : '#f8fafc'; // slate-900 : slate-50
        const gridColor = isDark ? '#1e293b' : '#e2e8f0'; // slate-800 : slate-200
        const textColor = isDark ? '#94a3b8' : '#64748b'; // slate-400 : slate-500
        const pathColor = color || (isDark ? '#ef4444' : '#dc2626'); // red-500 : red-600
        const pointColor = isDark ? '#ffffff' : '#0f172a'; // white : slate-900
        const startPointColor = '#3b82f6'; // blue-500

        // Responsive Canvas
        const parent = canvas.parentElement;
        if (parent) {
            canvas.width = parent.clientWidth;
            canvas.height = parent.clientHeight;
        }

        // Fill Background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw grid
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x < canvas.width; x += 40) { ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); }
        for (let y = 0; y < canvas.height; y += 40) { ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); }
        ctx.stroke();

        // Draw Path
        ctx.strokeStyle = pathColor;
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[path[0]].x, pts[path[0]].y);
        for (let i = 1; i < path.length; i++) {
            ctx.lineTo(pts[path[i]].x, pts[path[i]].y);
        }
        ctx.lineTo(pts[path[0]].x, pts[path[0]].y); // Loop
        ctx.stroke();

        // Draw Points
        pts.forEach((p, i) => {
            // Shadow for points
            ctx.shadowColor = 'rgba(0,0,0,0.2)';
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 2;

            ctx.fillStyle = i === 0 ? startPointColor : pointColor; // Start point blue
            ctx.beginPath();
            ctx.arc(p.x, p.y, i === 0 ? 8 : 6, 0, Math.PI * 2);
            ctx.fill();

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            // Well Label
            ctx.fillStyle = textColor;
            ctx.font = 'bold 10px monospace';
            ctx.fillText(`W-${100 + i}`, p.x + 12, p.y + 4);
        });
    };

    const solve = () => {
        if (isSolving) return;
        setIsSolving(true);
        setSolved(false); // Reset solved state for UI

        let currentRoute = [...route];
        let bestDist = bestDistance;
        let gen = 0;

        const startTime = Date.now();

        const step = () => {
            // Genetic Algorithm Simulation (Simplified 2-Opt Swap for visual effect)
            // We run multiple swaps per frame to speed it up
            for (let k = 0; k < 50; k++) {
                const i = Math.floor(Math.random() * points.length);
                const j = Math.floor(Math.random() * points.length);

                if (i !== j) {
                    const newRoute = [...currentRoute];
                    // Perform swap
                    const temp = newRoute[i];
                    newRoute[i] = newRoute[j];
                    newRoute[j] = temp;

                    const newDist = calculateDistance(points, newRoute);

                    // Selection: Keep if better
                    if (newDist < bestDist) {
                        currentRoute = newRoute;
                        bestDist = newDist;
                    }
                }
            }

            gen += 50;
            setGeneration(gen);
            setBestDistance(bestDist);
            setRoute(currentRoute);
            
            // Check theme for color
            const isDark = document.documentElement.classList.contains('dark');
            draw(points, currentRoute, isDark ? '#fbbf24' : '#d97706'); // Amber while solving

            // Stop condition (5.5 seconds for auto-start mode, 6 for manual)
            if (Date.now() - startTime < (autoStart ? 5500 : 6000)) {
                animationRef.current = requestAnimationFrame(step);
            } else {
                setIsSolving(false);
                setSolved(true);
                const isDark = document.documentElement.classList.contains('dark');
                draw(points, currentRoute, isDark ? '#10b981' : '#059669'); // Green when done

                if (autoStart && onComplete) {
                    setTimeout(onComplete, 1000); // Wait 1s after finish to show result
                } else if (!autoStart) {
                    toast.success("Optimization Complete", {
                        description: "Route efficiency improved by " + Math.round((1 - bestDist / initialDistance) * 100) + "%"
                    });
                }
            }
        };

        step();
    };

    // Metrics - More realistic calculations
    const improvement = initialDistance > 0 ? ((initialDistance - bestDistance) / initialDistance) * 100 : 0;
    // Realistic fuel calculations: assume ~200 pixels = 1 mile, 12mpg avg for trucks, $3.50/gal.
    // For an average route: ~50 miles saved per day = ~4 gallons saved = ~$14/day
    // Monthly: $420, Annually: ~$5,000 per truck
    const dailyMilesSaved = Math.round((initialDistance - bestDistance) * 0.005 * 10) / 10; // ~0.5% of pixel distance = miles
    const dailyGallonsSaved = dailyMilesSaved / 12;
    const dailyDollarsSaved = dailyGallonsSaved * 3.50;
    const estimatedAnnualSavings = Math.round(dailyDollarsSaved * 260); // 260 working days
    const estimatedTimeSavedHoursPerYear = Math.round(dailyMilesSaved * 2 * 260 / 60); // ~2 min per mile saved

    return (
        <div className={`w-full h-full p-6 rounded-3xl overflow-hidden flex flex-col bg-white dark:bg-slate-950 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 shadow-xl ${className}`}>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <div className="flex items-center space-x-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Cpu className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-pulse" />
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">GENETIC ROUTE SOLVER</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-mono mt-1">AI-DRIVEN FLEET OPTIMIZATION ENGINE • V4.2</p>
                </div>
                {!autoStart && (
                    <div className="flex space-x-2">
                        <Button variant="outline" onClick={resetSimulation} disabled={isSolving} className="border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                            <RotateCcw className="h-4 w-4 mr-2" /> Reset
                        </Button>
                        <Button
                            className={`${isSolving ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                            onClick={solve}
                            disabled={isSolving}
                        >
                            {isSolving ? (
                                <><Zap className="h-4 w-4 mr-2 animate-spin" /> Optimizing...</>
                            ) : (
                                <><Play className="h-4 w-4 mr-2" /> Run Solver</>
                            )}
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-y-auto">

                {/* Left: Visualization - PROMINENT ANIMATION - Made larger for mobile */}
                <div className="flex-1 relative rounded-2xl overflow-hidden min-h-[300px] lg:min-h-0 order-1 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                    <canvas ref={canvasRef} className="w-full h-full min-h-[300px]" />

                    {/* Overlay Stats */}
                    <div className="absolute top-4 left-4 p-4 rounded-xl font-mono text-xs border border-slate-200 dark:border-cyan-900/30 bg-white dark:bg-slate-900 shadow-lg">
                        <div className="text-slate-500 dark:text-cyan-400 font-bold mb-1">GENERATION</div>
                        <div className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">{generation.toLocaleString()}</div>
                    </div>

                    {/* Live Distance Tracker Overlay */}
                    <div className="absolute top-4 right-4 p-3 rounded-xl font-mono text-xs border border-slate-200 dark:border-amber-900/30 bg-white dark:bg-slate-900 shadow-lg">
                        <div className="text-slate-500 dark:text-amber-400 font-bold mb-1">ROUTE LENGTH</div>
                        <div className={`text-2xl font-bold ${isSolving ? 'text-amber-500 dark:text-amber-400 animate-pulse' : solved ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                            {Math.round(bestDistance)} <span className="text-xs text-slate-400">units</span>
                        </div>
                        {initialDistance > 0 && (
                            <div className="text-xs text-slate-400 mt-1">
                                Started: {Math.round(initialDistance)} (-{Math.round(improvement)}%)
                            </div>
                        )}
                    </div>

                    {/* Status Indicator */}
                    <div className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 dark:border-emerald-900/30 bg-white dark:bg-slate-900 shadow-lg">
                        {isSolving ? (
                            <>
                                <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
                                <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">Optimizing...</span>
                            </>
                        ) : solved ? (
                            <>
                                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">Complete</span>
                            </>
                        ) : (
                            <>
                                <div className="w-3 h-3 rounded-full bg-slate-400" />
                                <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">Ready</span>
                            </>
                        )}
                    </div>
                    
                    {/* Animation Legend */}
                    <div className="absolute bottom-4 left-4 flex items-center gap-4 text-xs bg-white/80 dark:bg-slate-900/80 p-2 rounded-lg backdrop-blur-sm border border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                            <span className="text-slate-600 dark:text-slate-400">Start</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-slate-900 dark:bg-white" />
                            <span className="text-slate-600 dark:text-slate-400">Wells</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className={`w-4 h-0.5 ${isSolving ? 'bg-amber-500' : solved ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="text-slate-600 dark:text-slate-400">Route</span>
                        </div>
                    </div>
                </div>

                {/* Right: CEO Metrics Dashboard - Show below animation on mobile */}
                <div className="w-full lg:w-96 space-y-4 order-2">

                    {/* Main Savings Card */}
                    <Card className={`border transition-all duration-500 ${solved ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-lg' : 'border-slate-200 dark:border-slate-800'} bg-white dark:bg-slate-900`}>
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs uppercase text-slate-500 dark:text-cyan-400 font-bold tracking-wider">Projected Annual Savings</span>
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${solved ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
                                    <DollarSign className={`h-5 w-5 ${solved ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`} />
                                </div>
                            </div>
                            <div className={`text-5xl font-black transition-all duration-1000 ${solved ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-600'}`}>
                                ${estimatedAnnualSavings.toLocaleString()}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">Per truck • Based on current fleet metrics & fuel costs</div>
                            {solved && (
                                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span className="text-xs text-emerald-600 dark:text-emerald-400">Optimization complete • Route efficiency improved by {Math.round(improvement)}%</span>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Efficiency Details */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 rounded-xl border border-slate-200 dark:border-blue-900/30 bg-white dark:bg-slate-900 shadow-sm">
                            <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 mb-2">
                                <TrendingDown className="h-4 w-4" />
                                <span className="text-xs font-bold uppercase tracking-wide">Distance Reduction</span>
                            </div>
                            <div className="text-3xl font-black text-blue-600 dark:text-blue-400">
                                -{Math.round(improvement)}%
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 mt-3 rounded-full overflow-hidden">
                                <div
                                    className="bg-blue-500 h-full transition-all duration-500 rounded-full"
                                    style={{ width: `${Math.min(improvement, 100)}%` }}
                                />
                            </div>
                        </div>

                        <div className="p-4 rounded-xl border border-slate-200 dark:border-purple-900/30 bg-white dark:bg-slate-900 shadow-sm">
                            <div className="flex items-center space-x-2 text-purple-600 dark:text-purple-400 mb-2">
                                <Clock className="h-4 w-4" />
                                <span className="text-xs font-bold uppercase tracking-wide">Time Saved</span>
                            </div>
                            <div className="text-3xl font-black text-purple-600 dark:text-purple-400">
                                {estimatedTimeSavedHoursPerYear} <span className="text-lg">hrs</span>
                            </div>
                            <div className="text-xs text-slate-500 dark:text-purple-300/70 mt-1">Per driver / year</div>
                        </div>
                    </div>

                    {/* AI Analysis Log */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 flex-1 p-4 font-mono text-xs overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-900">
                        <div className="text-slate-700 dark:text-cyan-400 mb-3 font-bold flex items-center">
                            <GitBranch className="h-3 w-3 mr-2" /> OPTIMIZATION LOG
                        </div>
                        <div className="space-y-2 text-slate-500 dark:text-slate-400 overflow-y-auto max-h-[200px] flex-1">
                            <div className="text-emerald-600 dark:text-emerald-400">
                                [SYSTEM] Solver initialized v4.2
                            </div>
                            <div>
                                [INPUT] {points.length} locations identified
                            </div>
                            <div>
                                [ANALYSIS] Calculating distance matrix...
                            </div>
                            {isSolving && (
                                <>
                                    <div className="text-amber-600 dark:text-amber-400 animate-pulse">
                                        [PROCESSING] Generation {generation}...
                                    </div>
                                    <div className="text-blue-600 dark:text-blue-400">
                                        [GENETIC] Mutating routes...
                                    </div>
                                    <div className="text-purple-600 dark:text-purple-400">
                                        [ANALYSIS] Evaluating fitness...
                                    </div>
                                </>
                            )}
                            {solved && (
                                <div className="text-emerald-600 dark:text-emerald-400 font-bold">
                                    [SUCCESS] Global optima found.
                                    <br />
                                    [RESULT] {Math.round(improvement)}% improvement achieved
                                    <br />
                                    [ACTION] Route pushed to mobile apps.
                                </div>
                            )}
                        </div>
                    </div>

                    {!autoStart && (
                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 shadow-lg shadow-blue-500/25">
                            <MapIcon className="h-4 w-4 mr-2" /> DEPLOY TO FLEET
                        </Button>
                    )}

                </div>
            </div>
        </div>
    );
}
